import React from 'react';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';
import { cn } from 'src/lib/utils';
import { getActionComponent, actionNeedsPayload } from '../actions';
import styles from './ManualActionsBar.module.css';

// Renders whichever manual (state-transition) actions the *current status*
// carries in the live flow-statuses response — GET /assignments/:id/
// flow-statuses returns each status with its own
// `manual_actions: [{ key, label }]`, and that's the source of truth for
// which bulk actions a tab offers, not anything hardcoded per tab. Each
// action's actual UI comes from the actions/ registry (looked up by
// stateMachineKey + key); an action listed here with no matching registered
// component is silently skipped rather than breaking the bar, since backend
// config can add a manual action before the frontend has built it.
//
// This intentionally does NOT cover the tabs' other action buttons (Refresh
// session, Resend test link, View scores) — those aren't state-transition
// actions, don't appear in manual_actions, and stay hardcoded in their own
// tab components.
//
// Rendered as a floating bar fixed to the bottom of the viewport (matching
// the reference design), not inline between the tab bar and the table.
// Stays mounted whenever this tab has any resolvable action at all — it
// slides in/out via `.bulkOn` based on selection instead of the caller
// mounting/unmounting it, which is what makes the slide transition actually
// animate rather than just appearing/disappearing.
//
// Actions that need a payload (assign_slot, reschedule_slot,
// interview_profile_update — see actionNeedsPayload) are deliberately
// excluded here: one date/time/interviewer picked once in this bar's modal
// can't correctly apply to several different candidates' own interview
// slots at once. Those stay reachable only through each row's own kebab
// menu (RowActionsCell), one candidate at a time — which is also why that
// kebab hides itself while any row here is selected (SelectionContext):
// the two are meant to be mutually exclusive entry points, not overlapping
// ones.
const ManualActionsBar = ({
  manualActions,
  stateMachineKey,
  selectedCount,
  mappingIds,
  candidateIds,
  opportunityId,
  assignmentId,
  onSuccess,
  onClear,
}) => {
  const resolvedActions = manualActions
    .map((action) => ({
      ...action,
      Component: getActionComponent(stateMachineKey, action.key),
    }))
    .filter((action) => action.Component && !actionNeedsPayload(action.key));

  if (resolvedActions.length === 0) return null;

  return (
    <div className={cn(styles.bulk, selectedCount > 0 && styles.bulkOn)}>
      <span className={styles.cnt}>
        <b className={styles.cntNum}>{selectedCount}</b> selected
      </span>
      <span className={styles.vr} />
      <div className={styles.actions}>
        {resolvedActions.map(({ key, label, Component }) => (
          <Component
            key={key}
            actionKey={key}
            label={label}
            mappingIds={mappingIds}
            candidateIds={candidateIds}
            opportunityId={opportunityId}
            assignmentId={assignmentId}
            onSuccess={onSuccess}
          />
        ))}
      </div>
      <button
        type="button"
        className={styles.iconBtn}
        onClick={onClear}
        aria-label="Clear selection"
      >
        <X />
      </button>
    </div>
  );
};

ManualActionsBar.propTypes = {
  manualActions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string,
    })
  ),
  stateMachineKey: PropTypes.string.isRequired,
  selectedCount: PropTypes.number,
  mappingIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  candidateIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  opportunityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  assignmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSuccess: PropTypes.func,
  onClear: PropTypes.func,
};

ManualActionsBar.defaultProps = {
  manualActions: [],
  selectedCount: 0,
  mappingIds: [],
  candidateIds: [],
  opportunityId: undefined,
  assignmentId: undefined,
  onSuccess: () => {},
  onClear: () => {},
};

export default ManualActionsBar;
